import React, { useState, useEffect } from 'react';
import { Quote, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { contentAPI, assessmentsAPI } from '../services/api';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState({ weeklyAssessments: 37 });
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();

  // Fetch testimonials and stats from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testimonialsData, statsData] = await Promise.all([
          contentAPI.getTestimonials(),
          assessmentsAPI.getPublicStats()
        ]);
        setTestimonials(testimonialsData || []);
        setStats(statsData || { weekly_assessments: 37 });
      } catch (error) {
        console.error('Failed to fetch testimonials:', error);
        // Fallback data
        setTestimonials([
          {
            id: "1",
            name: "Sarah De Vries",
            role: "HR Director, Tech Scale-up",
            initial: "S",
            photo: "https://images.unsplash.com/photo-1655249481446-25d575f1c054?w=200",
            quote: "The quickscan gave us immediate clarity on where to start. We implemented CV screening in 2 weeks and reduced time-to-hire by 60%.",
          },
          {
            id: "2",
            name: "Marc Janssens",
            role: "Head of L&D, Manufacturing",
            initial: "M",
            photo: "https://images.unsplash.com/photo-1576558656222-ba66febe3dec?w=200",
            quote: "Finally, a no-nonsense approach to AI in HR. The benchmark data opened our eyes, and the workshop delivered exactly what we needed.",
          },
          {
            id: "3",
            name: "Lisa Peeters",
            role: "CHRO, Financial Services",
            initial: "L",
            photo: "https://images.unsplash.com/photo-1655249493799-9cee4fe983bb?w=200",
            quote: "We went from 'AI is scary' to having two working use cases in one month. Highly recommended!",
          },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (testimonials.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const goToPrevious = () => {
    if (testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    if (testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  if (loading) {
    return (
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('whatOurUsersSay')}
          </h2>
          <p className="text-lg text-gray-600">
            {t('trustedByTeams')}
          </p>
        </div>

        {/* Testimonials Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="w-full flex-shrink-0 px-4"
                >
                  <div className="bg-white rounded-2xl p-8 shadow-lg">
                    <div className="flex items-start mb-6">
                      {testimonial.photo ? (
                        <img 
                          src={testimonial.photo} 
                          alt={testimonial.name}
                          className="w-14 h-14 rounded-full object-cover mr-4"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xl font-bold mr-4">
                          {testimonial.initial}
                        </div>
                      )}
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">
                          {testimonial.name}
                        </h4>
                        <p className="text-gray-500 text-sm">
                          {testimonial.role}
                        </p>
                      </div>
                      <Quote className="ml-auto h-8 w-8 text-blue-200" />
                    </div>
                    <p className="text-gray-700 text-lg leading-relaxed">
                      "{testimonial.quote}"
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-center items-center mt-8 space-x-4">
            <Button
              variant="outline"
              size="icon"
              onClick={goToPrevious}
              className="rounded-full"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={`dot-${index}`}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentIndex ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={goToNext}
              className="rounded-full"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Weekly Quickscan Badge */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse" />
            {stats.weekly_assessments || stats.weeklyAssessments || 37} {language === 'nl' ? 'quickscans voltooid deze week' : 'quickscans completed this week'}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
